import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../components/UIToolkit/Animations/JumpingDots";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE, MOTION, NEUTRAL, SHADOW, SURFACE } from "../../../../theme/tokens";

/** Card accent: `accent` is the brand (a move), `warning` an over-staffed cell, `success` handled. */
export type OpportunityCardTone = "accent" | "warning" | "success";

type Props = {
	/** The card's headline — the coordinates this opportunity is about. */
	head: ReactNode;
	/** Trailing slot on the headline row — a `Requested` chip, an "over by N" chip. */
	headEnd?: ReactNode;
	/** Micro-caption under the headline: who is on that shift. Already translated. */
	caption?: string;
	tone: OpportunityCardTone;
	isLoading: boolean;
	isError: boolean;
	errorLabel: string;
	/** Shown when the fetch succeeded with nobody in it. */
	emptyLabel: string;
	rowCount: number;
	/** People beyond the ones rendered — stated rather than silently dropped. */
	moreCount: number;
	/**
	 * Expand / collapse the rest of the list in place. When given, the "+N more" line becomes a real
	 * control instead of a statement — a count you cannot act on tells you people exist and then offers
	 * no way to see them. Omit it and the line stays a plain caption, so a caller with nowhere to expand
	 * to is unaffected.
	 */
	onToggleMore?: () => void;
	/** Whether the full list is currently shown — flips the control's label. */
	isExpanded?: boolean;
	onHoverChange: (hovered: boolean) => void;
	/** The person rows. */
	children: ReactNode;
	dataTestId: string;
};

/**
 * One recommendation as a card: the coordinates it is about, who it concerns, and the action per
 * person — the shape the Move board uses.
 *
 * The card owns the surface, the tone accent, the hover hand-off that highlights the affected cells in
 * the gaps table, and the three states of the caller's query. It deliberately does NOT fetch, which is
 * what lets the section bound how many queries run at once.
 */
const OpportunityCard: FC<Props> = ({
	head,
	headEnd,
	caption,
	tone,
	isLoading,
	isError,
	errorLabel,
	emptyLabel,
	rowCount,
	moreCount,
	onToggleMore,
	isExpanded,
	onHoverChange,
	children,
	dataTestId,
}) => {
	const { t } = useTranslation();

	return (
		<Box
			data-testid={dataTestId}
			onMouseEnter={() => onHoverChange(true)}
			onMouseLeave={() => onHoverChange(false)}
			sx={(theme) => {
				const accent = tone === "accent" ? theme.palette.primary.main : theme.palette[tone].main;
				return {
					display: "flex",
					flexDirection: "column",
					borderRadius: 1.5,
					backgroundColor: SURFACE,
					border: `1px solid ${NEUTRAL[200]}`,
					boxShadow: SHADOW.PANEL_SOFT,
					overflow: "hidden",
					transition: theme.transitions.create(["box-shadow", "border-color", "transform"], {
						duration: MOTION.DURATION.HOVER,
						easing: MOTION.EASE_OUT,
					}),
					"@media (hover: hover) and (pointer: fine)": {
						"&:hover": { borderColor: alpha(accent, 0.4), boxShadow: SHADOW.PANEL_SOFT_HOVER, transform: "translateY(-1px)" },
					},
					"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
					// Top rule carries the card's tone, so a grid of cards reads by kind at a glance without
					// tinting whole surfaces.
					borderTop: `2px solid ${alpha(accent, tone === "success" ? 0.9 : 0.55)}`,
				};
			}}
		>
			<Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: `1px solid ${NEUTRAL[150]}` }}>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
					<Box sx={{ minWidth: 0 }}>{head}</Box>
					{headEnd}
				</Box>
				{caption && (
					<Typography sx={{ mt: 0.5, fontSize: FONT_SIZE.CAPTION, color: NEUTRAL[500] }} data-testid={`${dataTestId}-caption`}>
						{caption}
					</Typography>
				)}
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 1, py: 1, flex: 1 }}>
				{isLoading && (
					<Box sx={{ display: "flex", justifyContent: "center", py: 2 }} data-testid={`${dataTestId}-loading`}>
						<JumpingDots />
					</Box>
				)}
				{isError && (
					<Typography color="error" sx={{ px: 1, py: 1.5, fontSize: FONT_SIZE.SECONDARY }} data-testid={`${dataTestId}-error`}>
						{errorLabel}
					</Typography>
				)}
				{!isLoading && !isError && rowCount === 0 && (
					<Typography sx={{ px: 1, py: 1.5, fontSize: FONT_SIZE.SECONDARY, color: NEUTRAL[500] }} data-testid={`${dataTestId}-empty`}>
						{emptyLabel}
					</Typography>
				)}
				{children}
				{onToggleMore
					? (moreCount > 0 || isExpanded) && (
							<Box sx={{ pt: 0.5 }}>
								<SystemButton
									buttonVariant="borderless"
									size="compact"
									noFullWidthMobile
									onClick={onToggleMore}
									aria-expanded={isExpanded}
									data-testid={`${dataTestId}-toggle-more`}
								>
									{isExpanded ? t("hiring.opportunity.showFewerStaff") : t("hiring.opportunity.moreStaff", { count: moreCount })}
								</SystemButton>
							</Box>
						)
					: moreCount > 0 && (
							<Typography sx={{ px: 1, pt: 0.25, fontSize: FONT_SIZE.CAPTION, color: NEUTRAL[500] }} data-testid={`${dataTestId}-more`}>
								{t("hiring.opportunity.moreStaff", { count: moreCount })}
							</Typography>
						)}
			</Box>
		</Box>
	);
};

export default OpportunityCard;
